import { Analytics } from 'analytics';
import type { AnalyticsInstance, AnalyticsPlugin } from 'analytics';

interface AnalyticsConfig {
  merchantId: string;
  plugins: AnalyticsPlugin[];
}

/**
 * Os plugins (Meta Pixel, GTM, Clarity) são classes, mas a biblioteca
 * `analytics` copia os métodos e os chama sem o `this` — `loaded()` quebrava com
 * "Cannot read properties of undefined (reading 'isLoaded')", a inicialização
 * falhava e nenhum evento chegava ao Pixel nem ao GTM (PRAT-19). Aqui cada
 * método vira função ligada à própria instância.
 */
export function bindPlugin(plugin: AnalyticsPlugin): AnalyticsPlugin {
  const bound: Record<string, unknown> = { ...plugin };
  let proto = Object.getPrototypeOf(plugin);
  while (proto && proto !== Object.prototype) {
    for (const key of Object.getOwnPropertyNames(proto)) {
      const value = (plugin as unknown as Record<string, unknown>)[key];
      if (key !== 'constructor' && typeof value === 'function' && !(key in bound)) {
        bound[key] = (value as (...args: unknown[]) => unknown).bind(plugin);
      }
    }
    proto = Object.getPrototypeOf(proto);
  }
  return bound as unknown as AnalyticsPlugin;
}

class AnalyticsSingleton {
  private static instance: AnalyticsSingleton;
  private analyticsInstance: AnalyticsInstance | null = null;
  private currentConfig: AnalyticsConfig | null = null;
  private isInitializing = false;

  private constructor() {}

  static getInstance(): AnalyticsSingleton {
    if (!AnalyticsSingleton.instance) {
      AnalyticsSingleton.instance = new AnalyticsSingleton();
    }
    return AnalyticsSingleton.instance;
  }

  async getAnalytics(config: AnalyticsConfig): Promise<AnalyticsInstance> {
    // Se já está inicializando, aguarda
    if (this.isInitializing) {
      return new Promise(resolve => {
        const checkInitialization = () => {
          if (!this.isInitializing) {
            resolve(this.analyticsInstance);
          } else {
            setTimeout(checkInitialization, 100);
          }
        };
        checkInitialization();
      });
    }

    // Se já foi inicializado com a mesma configuração, retorna a instância existente
    if (this.analyticsInstance && this.currentConfig?.merchantId === config.merchantId) {
      return this.analyticsInstance;
    }

    // Se está inicializando com configuração diferente, aguarda
    if (this.isInitializing && this.currentConfig?.merchantId !== config.merchantId) {
      return new Promise(resolve => {
        const checkInitialization = () => {
          if (!this.isInitializing) {
            resolve(this.analyticsInstance);
          } else {
            setTimeout(checkInitialization, 100);
          }
        };
        checkInitialization();
      });
    }

    // Inicializa nova instância
    this.isInitializing = true;
    this.currentConfig = config;

    try {
      this.analyticsInstance = Analytics({
        app: 'pratigo-app',
        version: '1.0.0',
        plugins: config.plugins.map(bindPlugin),
        debug: import.meta.env.MODE === 'development',
      });

      console.log('✅ Analytics inicializado com sucesso para merchant:', config.merchantId);
      return this.analyticsInstance;
    } catch (error) {
      console.error('❌ Erro ao inicializar Analytics:', error);
      this.analyticsInstance = null;
      this.currentConfig = null;
      return null;
    } finally {
      this.isInitializing = false;
    }
  }

  isReady(): boolean {
    return this.analyticsInstance !== null && !this.isInitializing;
  }

  getCurrentMerchantId(): string | null {
    return this.currentConfig?.merchantId || null;
  }
}

export const analyticsSingleton = AnalyticsSingleton.getInstance();
